import { useCallback, useEffect, useRef, useState } from 'react';

type CustomScrollbarState = {
  visible: boolean;
  thumbHeight: number;
  thumbTop: number;
};

const HIDDEN_STATE: CustomScrollbarState = {
  visible: false,
  thumbHeight: 0,
  thumbTop: 0,
};

export const useCustomScrollbar = <T extends HTMLElement = HTMLElement>(active: boolean, updateKey?: number) => {
  const scrollRef = useRef<T | null>(null);
  const [customScrollbar, setCustomScrollbar] = useState<CustomScrollbarState>(HIDDEN_STATE);

  const updateCustomScrollbar = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const { clientHeight, scrollHeight, scrollTop } = el;
    const canScroll = scrollHeight > clientHeight + 1;

    if (!canScroll) {
      setCustomScrollbar((prev) => (prev.visible ? HIDDEN_STATE : prev));
      return;
    }

    const thumbHeight = Math.max((clientHeight / scrollHeight) * clientHeight, 24);
    const maxScrollTop = scrollHeight - clientHeight;
    const maxThumbTop = clientHeight - thumbHeight;
    const thumbTop = maxScrollTop > 0 ? (scrollTop / maxScrollTop) * maxThumbTop : 0;

    setCustomScrollbar({
      visible: true,
      thumbHeight,
      thumbTop,
    });
  }, []);

  useEffect(() => {
    if (!active) return;
    const el = scrollRef.current;
    if (!el) return;

    const handleUpdate = () => updateCustomScrollbar();
    handleUpdate();
    const rafId = requestAnimationFrame(handleUpdate);

    el.addEventListener('scroll', handleUpdate, { passive: true });
    window.addEventListener('resize', handleUpdate);

    const resizeObserver = new ResizeObserver(handleUpdate);
    resizeObserver.observe(el);
    const content = el.firstElementChild;
    if (content) resizeObserver.observe(content);

    return () => {
      cancelAnimationFrame(rafId);
      el.removeEventListener('scroll', handleUpdate);
      window.removeEventListener('resize', handleUpdate);
      resizeObserver.disconnect();
    };
  }, [active, updateKey, updateCustomScrollbar]);

  return { scrollRef, customScrollbar };
};
